// CurrencyInput — input numérico padrão Alamo com máscara BRL automática.
// Substitui <Input type="number"> em qualquer campo monetário (sem stepper
// nativo, sem precisar formatar manualmente).
//
// Contrato:
//   value: number | undefined          ← valor numérico (centavos truncados)
//   onChange: (n: number | undefined)  ← callback recebe number ou undefined (vazio)
//
// Display: "1.234,56" (pt-BR Intl.NumberFormat). Aceita digitação livre,
// remove qualquer não-dígito, divide por 100 pra ter 2 casas.

import { forwardRef, useMemo } from 'react';

import { maskCurrency } from '@alamo/utils';

import { Input, type InputProps } from '../Input/Input';

export interface CurrencyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  /** Valor numérico. undefined renderiza vazio. */
  value?: number;
  /** Callback recebe number ou undefined quando o input fica vazio. */
  onChange?: (value: number | undefined) => void;
  /** Símbolo de prefixo (default: "R$"). Passar null pra esconder. */
  currencySymbol?: string | null;
}

function numberToRawDigits(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return '';
  // Mantém 2 casas decimais. 1234.56 → "123456"
  return Math.round(n * 100).toString();
}

function digitsToNumber(digits: string): number | undefined {
  if (!digits) return undefined;
  const parsed = parseInt(digits, 10);
  if (Number.isNaN(parsed)) return undefined;
  return parsed / 100;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput({ value, onChange, currencySymbol = 'R$', leftIcon, ...rest }, ref) {
    const display = useMemo(() => {
      const raw = numberToRawDigits(value);
      if (!raw) return '';
      return maskCurrency(raw);
    }, [value]);

    const symbolNode = currencySymbol ? (
      <span
        aria-hidden="true"
        className="select-none text-xs font-body font-semibold text-secondary-700"
      >
        {currencySymbol}
      </span>
    ) : null;

    return (
      <Input
        {...rest}
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        leftIcon={leftIcon ?? symbolNode ?? undefined}
        value={display}
        onChange={(e) => {
          const raw = e.target.value.replace(/\D/g, '');
          const next = digitsToNumber(raw);
          onChange?.(next);
        }}
      />
    );
  },
);
